import axios from 'axios';
import { erc20Abi, formatUnits } from 'viem';

import { mockPositions } from '@/data';
import { PortfolioSummary } from '@/types';
import { publicClient } from '@/utils/approvals.utils';
import axiosClient from './axiosClient';
import { createMockError, shouldSimulateError, simulateNetworkDelay } from './mockHelpers';

import { API_ENDPOINTS } from '@/constants/api.constants';
import {
  NATIVE_USDC_CONTRACT_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  PUSD_DECIMALS,
  USDC_E_CONTRACT_ADDRESS,
  USDC_E_DECIMALS,
} from '@/constants/tokens.constants';

export const portfolioApi = {
  getBridgeDepositAddress: async (): Promise<{
    destinationAddress: string;
    addresses: { evm?: string; svm?: string; btc?: string; tvm?: string };
    raw?: any;
    minimums: { chainId: number; chainName: string; symbol: string; minCheckoutUsd: number }[];
  } | null> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.FIAT.BRIDGE_DEPOSIT_ADDRESS);
    return data?.data || null;
  },

  getPositions: async (
    user: string,
    eventId?: string,
    limit: number = 500,
    offset: number = 0,
    title?: string,
    sortBy?: string,
  ): Promise<any> => {
    if (!user) return [];

    const { data } = await axiosClient.get(API_ENDPOINTS.MARKETS_POSITIONS, {
      params: {
        user,
        eventId,
        limit,
        offset,
        title,
        sortBy,
      },
    });

    // Handle generic wrapper formats or standard array returns
    return data?.data || data || [];
  },

  /**
   * Get user's 'other' activity/positions for an event
   */
  getOtherPositions: async (user: string, eventId: string): Promise<any> => {
    if (!user || !eventId) return [];

    const { data } = await axiosClient.get(API_ENDPOINTS.CLOB_DATA.OTHER, {
      params: {
        id: eventId,
        user,
      },
    });

    return data;
  },

  /**
   * Get total portfolio value for a user
   */
  getUserValue: async (user: string): Promise<any> => {
    if (!user) return null;

    const { data } = await axiosClient.get(API_ENDPOINTS.CLOB_DATA.VALUE, {
      params: { user },
    });

    return data;
  },

  getHistory: async (
    user: string,
    limit: number = 100,
    offset: number = 0,
    title?: string,
    sortDirection?: string,
    sortBy?: string,
    type?: string,
    start?: number,
    end?: number,
    side?: string,
  ): Promise<any> => {
    if (!user) return [];

    const { data } = await axiosClient.get(API_ENDPOINTS.MARKETS_ACTIVITY_PORTFOLIO, {
      params: { user, limit, offset, title, sortDirection, sortBy, type, start, end, side },
    });

    return data;
  },

  getActions: async (): Promise<any[]> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.PORTFOLIO_ACTIONS);
    return data?.data || [];
  },

  getSortOptions: async (): Promise<any[]> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.PORTFOLIO_SORT_OPTIONS);
    return data?.data || [];
  },

  /**
   * Get portfolio summary
   */
  getSummary: async (): Promise<PortfolioSummary> => {
    await simulateNetworkDelay(150, 400);

    if (shouldSimulateError()) {
      throw createMockError('Failed to fetch portfolio summary', 500);
    }

    const positions = mockPositions;
    const totalValue = positions.reduce((sum, p) => sum + p.shares * p.currentPrice, 0);
    const totalCost = positions.reduce((sum, p) => sum + p.shares * p.avgPrice, 0);
    const totalPnl = totalValue - totalCost;
    const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

    return {
      totalValue,
      totalPnl,
      totalPnlPercent,
      positionsCount: positions.length,
      openOrdersCount: 0,
      cashBalance: 0,
      pusdBalance: 0,
      legacyUsdcBalance: 0,
    };
  },

  /**
   * Fetches the native USDC balance (Circle CCTP, 0x3c499...).
   * Coinbase Onramp and most exchanges now send this instead of USDC.e.
   */
  getNativeUsdcBalance: async (address: string): Promise<number> => {
    if (!address || !publicClient) return 0;
    try {
      const balance = await publicClient.readContract({
        address: NATIVE_USDC_CONTRACT_ADDRESS as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address as `0x${string}`],
        authorizationList: undefined,
      });
      return Number(balance) / 1e6;
    } catch {
      return 0;
    }
  },

  /**
   * Fetches the pUSD balance for a given address (v2 collateral).
   */
  getPusdBalance: async (address: string): Promise<number> => {
    try {
      const balance = await publicClient.readContract({
        address: PUSD_CONTRACT_ADDRESS as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address as `0x${string}`],
        authorizationList: undefined,
      });
      // pUSD has 6 decimals (same as USDC.e)
      const val = Number(balance) / 1e6;
      return val;
    } catch (error) {
      console.error('[PortfolioApi] Failed to fetch pUSD balance:', error);
      return 0;
    }
  },

  /**
   * Fetches only the legacy USDC.e balance.
   */
  getLegacyUsdcBalance: async (walletAddress: string): Promise<number> => {
    if (!walletAddress || !publicClient) return 0;
    try {
      const balance = await publicClient.readContract({
        address: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [walletAddress as `0x${string}`],
        authorizationList: undefined,
      });
      const val = Number(balance) / 1e6;
      return val;
    } catch (error) {
      console.error('[PortfolioApi] Failed to fetch legacy USDC.e balance:', error);
      return 0;
    }
  },

  /**
   * Get pUSD balance for a wallet address on Polygon.
   * V2: pUSD (Polymarket USD) is the new trading collateral replacing USDC.e.
   * Directly reads from the blockchain using publicClient.
   */
  getUsdcBalance: async (walletAddress: string): Promise<number> => {
    if (!walletAddress || !publicClient) return 0;

    try {
      const balance = await publicClient.readContract({
        address: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [walletAddress as `0x${string}`],
        authorizationList: undefined,
      });
      const val = Number(balance) / 1e6;
      return val;
    } catch (error) {
      console.error('[PortfolioApi] Failed to fetch USDC.e balance:', error);
      return 0;
    }
  },

  getCategories: async (): Promise<any[]> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.PORTFOLIO_CATEGORIES);
    return data?.data?.allPositionSortOptions || [];
  },

  getDepositAddress: async (): Promise<string | null> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.PORTFOLIO_DEPOSIT_ADDRESS);
    return data?.data?.depositAddress || data?.depositAddress || null;
  },

  getGeoblockStatus: async (): Promise<{
    blocked: boolean;
    ip: string;
    country: string;
    region: string;
  }> => {
    const { data } = await axios.get(API_ENDPOINTS.GEOBLOCK);
    return data;
  },

  getEventDetails: async (eventId: string): Promise<any> => {
    const { data } = await axios.get(API_ENDPOINTS.GAMMA_EVENT(eventId));
    return data;
  },
};
